<template>
  <div class="chart-root">
  </div>
</template>

<script>
  import { i18n } from '@js/i18n'

  import * as d3Array from 'd3-array'
  import * as d3Selection from 'd3-selection'
  import * as d3Scale from 'd3-scale'
  import * as d3Axis from 'd3-axis'
  import * as d3Shape from 'd3-shape'
  import * as d3Transition from 'd3-transition'
  import * as d3Ease from 'd3-ease'
  // import * as d3 from 'd3'
  import { chunk } from 'lodash'
  import moment from 'moment'
  import CurrencyRound from '@com/mixins/currency.round.mixin'
  const d3 = Object.assign({}, d3Array, d3Selection, d3Axis, d3Shape, d3Scale, d3Transition, d3Ease)

  export default {
    name: 'chart-renderer',
    mixins: [CurrencyRound],
    props: {
      data: {
        type: Array,
        default: () => []
      },
      volume: {
        type: Array,
        default: () => []
      },
      currency: {
        type: String,
        default: 'USD'
      },
      scale: {
        type: String,
        default: 'hour'
      },
      requiredTicks: {
        type: Array,
        default: () => []
      },
      precision: {
        type: Number,
        default: 0
      }
    },

    data: () => ({
      fullHeight: 250,
      height: 250,
      barTicks: 4 // in points
    }),

    watch: {
      data (data) {
        this.render()
      },
      volume (data) {
        this.render()
      },
      height (value) {
        this.render()
      }
    },

    mounted (...args) {
      this.render()
      window.addEventListener('resize', this.render)
    },

    beforeDestroy () {
      window.removeEventListener('resize', this.render)
    },

    computed: {
      normalizedData () {
        return this.data.map(item => ({
          time: moment(item.timestamp).toDate(),
          value: parseFloat(parseFloat(item.value).toFixed(this.precision))
        }))
      },
      normalizedVolume () {
        return this.volume.map(item => ({
          time: moment(item.timestamp).toDate(),
          value: parseFloat(parseFloat(item.value).toFixed(this.precision))
        }))
      },
      heightDifference () {
        return this.fullHeight - this.height
      }
    },

    methods: {
      clear () {
        d3.select('svg.chart').remove()
      },

      getDimensions () {
        const parentElement = this.$el.parentElement
        return {
          width: parentElement.clientWidth,
          height: this.height
        }
      },

      getMaxAndMin () {
        const arr = this.data.map(item => item.value)
        const max = Math.max(...arr, ...this.requiredTicks)
        const min = this.requiredTicks && this.requiredTicks.length ? 0 : Math.min(...arr)
        return { max, min }
      },

      addDomainPadding (domain) {
        let [min, max] = domain
        const diff = (max - min) || 0.0001
        const padding = diff * 0.05
        min = min - padding
        max = max + padding
        return [min, max]
      },

      formatMoney (amount) {
        const curSym = ({ 'USD': 'USD' })[this.currency]
        const moneyFormats = {
          'en': (curSym, amount) => `${curSym}${amount}`
        }
        return moneyFormats['en'](curSym, amount.toFixed(this.precision))
      },

      render () {
        this.clear(this.$el)

        // Setup the data
        const scale = this.scale
        const data = this.normalizedData
        const volume = chunk(this.normalizedVolume, this.barTicks).map(item => {
          const itemLength = item.length
          let sum = 0
          let defaultDate = 0

          for (let i = 0; i < itemLength; i++) {
            sum += item[i].value
            defaultDate += Date.parse(item[i].time)
          }

          return {
            time: new Date(defaultDate / itemLength),
            value: sum / itemLength
          }
        })

        const { max, min } = this.getMaxAndMin(data)
        if (!data[0] || !data[data.length - 1]) return

        const firstDate = data[0].time
        const lastDate = data[data.length - 1].time

        // Setup svg
        const className = 'chart'
        const yAxisTickWidth = this.formatMoney(max).length * 9 + 5
        const margin = { top: 2, left: yAxisTickWidth, bottom: 32, right: 0 }
        const dimensions = this.getDimensions(this.$el)
        const width = dimensions.width // - margin.right - margin.left
        let height = dimensions.height - margin.top - margin.bottom
        const barsHeight = 35
        const heightDifference = this.heightDifference

        const viewWidth = width + margin.right + margin.left
        const viewHeight = height + margin.top + margin.bottom + barsHeight
        const svg = d3.select(this.$el)
          .append('svg')
          .attr('width', '100%')
          .attr('viewBox', `0 0 ${viewWidth} ${viewHeight}`)
          .attr('preserveAspectRatio', 'xMinYMin')
          .attr('class', className)
          .append('g')
          .attr('transform', `translate(${margin.left}, ${margin.top})`)

        // Define domains
        const y = d3.scaleLinear()
          .range([height, 0])
          .domain(this.addDomainPadding([min, max]))

        const x = d3.scaleTime()
          .range([0, width])
          .domain([firstDate, lastDate])

        // Render the line and area
        const line = d3.line()
          .x((d) => x(d.time))
          .y((d) => y(d.value))

        const path = svg.append('g')
          .append('path')
          .attr('class', `${className}__line`)
          .attr('d', line(data))

        const totalLength = path.node().getTotalLength()

        path
          .attr('stroke-dasharray', totalLength + ' ' + totalLength)
          .attr('stroke-dashoffset', totalLength)
          .transition()
          .duration(500)
          .ease(d3.easeLinear)
          .attr('stroke-dashoffset', 0)

        // Render x-axis
        const xAxisTickFormat = ({
          hour: 'hh:mm a',
          day: 'hh:mm a',
          week: 'ddd',
          month: 'MMM DD',
          year: 'MMM DD, YY',
          all: 'MM/DD/YYYY'
        })[scale]

        const xAxis = d3.axisBottom(x)
          .tickValues([
            data[parseInt(data.length * 0.10)].time,
            data[parseInt(data.length * 0.36)].time,
            data[parseInt(data.length * 0.63)].time,
            data[parseInt(data.length * 0.90)].time
          ])
          .tickFormat((d) => moment(d).format(xAxisTickFormat))
          .tickSize(0)
          .tickPadding(0)

        svg.append('g')
          .attr('class', `${className}__x-axis`)
          .attr('transform', `translate(0, ${viewHeight + (min === max ? -barsHeight : heightDifference)})`)
          .call(xAxis)

        // Render y-axis
        const ticksCount = 7
        const ticksSteps = []
        const additionalTicksSteps = []

        let diff = (max - min) / (ticksCount - 2)

        if (max > min) {
          if (min > diff * 2) {
            diff = (max - min) / (ticksCount - 3)
            for (let i = 0; i < ticksCount - 3; i++) {
              ticksSteps.push(((diff) * i) + min)
            }
            additionalTicksSteps.push({
              offset: barsHeight,
              value: this.roundCurrency(min - diff, this.currency)
            }, {
              offset: barsHeight * 2,
              value: this.roundCurrency(min - (diff * 2), this.currency)
            })
            this.height = 215
          } else if (min > diff && min < diff * 2) {
            diff = (max - min) / (ticksCount - 2)
            for (let i = 0; i < ticksCount - 2; i++) {
              ticksSteps.push(((diff) * i) + min)
            }
            additionalTicksSteps.push({
              offset: barsHeight,
              value: this.roundCurrency(min - diff, this.currency)
            })
            this.height = 250
          } else if (min === 0) {
            for (let i = 0; i < ticksCount - 1; i++) {
              ticksSteps.push((((max - min) / (ticksCount - 1)) * i) + min)
            }
            this.height = 300
          } else {
            for (let i = 0; i < ticksCount - 2; i++) {
              ticksSteps.push((((max - min) / (ticksCount - 2)) * i) + min)
            }
            additionalTicksSteps.push({
              offset: barsHeight,
              value: this.roundCurrency(0, this.currency)
            })
            this.height = 250
          }
        } else {
          const val = max / 3
          this.height = 280
          additionalTicksSteps.push(
            { value: this.roundCurrency(max - (val * 3), this.currency), step: 0 },
            { value: this.roundCurrency(max - (val * 2), this.currency), step: 1 },
            { value: this.roundCurrency(max - (val * 1), this.currency), step: 2 },
            { value: this.roundCurrency((val * 1) + max, this.currency), step: 4 },
            { value: this.roundCurrency((val * 2) + max, this.currency), step: 5 },
            { value: this.roundCurrency((val * 3) + max, this.currency), step: 6 }
          )
        }

        if (additionalTicksSteps.length && min !== max) {
          additionalTicksSteps.forEach(item => {
            const additionalLine = svg.append('g')
              .attr('y', height)
              .attr('class', `${className}__y-axis`)

            additionalLine.append('text')
              .attr('text-anchor', 'middle')
              .attr('y', height + margin.top + item.offset)
              .attr('x', -82)
              .text(`${i18n.c(item.value)} ${this.currency}`)

            additionalLine.append('line')
              .attr('x1', 0)
              .attr('y1', height + margin.top + item.offset)
              .attr('x2', width + margin.right)
              .attr('y2', height + margin.top + item.offset)
          })
        } else if (additionalTicksSteps.length && min === max) {
          const stepHeight = 40
          additionalTicksSteps.forEach((item, i) => {
            const additionalLine = svg.append('g')
              .attr('y', height)
              .attr('class', `${className}__y-axis`)

            additionalLine.append('text')
              .attr('text-anchor', 'middle')
              .attr('y', height + margin.top - (stepHeight * item.step))
              .attr('x', -78)
              .text(`${i18n.c(item.value)} ${this.currency}`)

            additionalLine.append('line')
              .attr('x1', 0)
              .attr('y1', height + margin.top - (stepHeight * item.step))
              .attr('x2', width + margin.right)
              .attr('y2', height + margin.top - (stepHeight * item.step))
          })
        }
        const yAxisLine = d3.axisLeft(y)
          .tickValues([ ...ticksSteps, max ].concat(this.requiredTicks))
          .tickFormat((d) => `${this.roundCurrency(d, this.currency)} ${this.currency}`) // unbreakable space this.formatMoney(d)
          .tickSizeInner(-(width + margin.right))
          .tickSizeOuter(0)
          .tickPadding(25)

        svg.append('g')
          .attr('class', `${className}__y-axis`)
          .call(yAxisLine)
          .selectAll('line')

        svg.append('g')
          .attr('height', barsHeight)
          .selectAll('rect')
          .data(volume)
          .enter().append('rect')
          .attr('fill', '#697b8c')
          .attr('opacity', '0.5')
          .attr('width', width / volume.length)
          .attr('height', function (data) {
            if ((barsHeight / d3.max(volume.map(item => item.value))) * data.value) {
              return (barsHeight / d3.max(volume.map(item => item.value))) * data.value
            } else {
              return 0
            }
          })
          .attr('x', function (data, i) {
            return i * width / volume.length
          })
          .attr('y', function (data) {
            if ((barsHeight / d3.max(volume.map(item => item.value))) * data.value) {
              return viewHeight - margin.bottom + heightDifference - (barsHeight / d3.max(volume.map(item => item.value))) * data.value
            } else {
              return 0
            }
          })

        svg.append('rect')
           .attr('height', 1)
           .attr('width', width)
           .attr('fill', '#b24b45')
           .attr('opacity', '0.5')
           .attr('y', viewHeight - margin.bottom + heightDifference)

        // Tip
        const tip = svg.append('g')
          .attr('class', `${className}__tip`)

        // Tip line
        tip.append('line')
          .attr('class', `${className}__tip-line`)
          .attr('x1', 0)
          .attr('y1', 15)
          .attr('x2', 0)
          .attr('y2', viewHeight - margin.bottom)

        // Tip circle
        const tipCircle = tip.append('circle')
          .attr('class', `${className}__tip-circle`)
          .attr('cx', 0)
          .attr('r', 5)

        // Tip text box
        const tipTextBox = tip.append('g')

        tipTextBox.append('rect')
          .attr('class', `${className}__tip-text-box`)
          .attr('width', 150)
          .attr('height', 75)
          .attr('transform', 'translate(-75, -38)')
          .attr('rx', 3)
          .attr('ry', 3)

        const tipPriceText = tipTextBox.append('text')
          .attr('class', `${className}__tip-text-price`)
          .attr('text-anchor', 'middle')
          .attr('y', -15)

        const tipVolumeText = tipTextBox.append('text')
          .attr('class', `${className}__tip-text-price`)
          .attr('text-anchor', 'middle')
          .attr('y', 25)

        const tipTimeText = tipTextBox.append('text')
          .attr('class', `${className}__tip-text-time`)
          .attr('text-anchor', 'middle')
          .attr('y', 0)

        // Tip motion capture area
        const motionCaptureArea = svg.append('rect')
          .attr('class', `${className}__tip-motion-capture-area`)
          .attr('width', width)
          .attr('height', height - 25)
          .attr('transform', 'translate(0, 25)')

        // Tip Mouse events
        for (const event of ['mouseenter', 'touchenter']) {
          motionCaptureArea.on(event, function () {
            tip.classed(`${className}__tip--show`, true)
          })
        }

        for (const event of ['mousemove', 'touchmove']) {
          motionCaptureArea.on(event, () => {
            tip.classed(`${className}__tip--hidden`, false)
            const x0 = x.invert(d3.mouse(svg.node())[0])
            const bisectDate = d3.bisector(d => d.time).left
            const bisectIndex = bisectDate(data, x0, 1)
            const bisectIndexOne = bisectDate(volume, x0, 1)
            const d0Price = data[bisectIndex - 1]
            const d1Price = data[bisectIndex]
            const d0Volume = volume[bisectIndexOne - 1]
            const d1Volume = volume[bisectIndexOne]
            const nearestPointPrice = x0 - d0Price.time > d1Price.time - x0 ? d1Price : d0Price
            const nearestPointVolume = x0 - d0Volume.time > d1Volume.time - x0 ? d1Volume : d0Volume
            // Change text of the tooltip
            tipPriceText.text(`${i18n.c(nearestPointPrice.value)} ${this.currency}`)
            tipTimeText.text(moment(nearestPointPrice.time).format('MM/DD/YYYY hh:mm a'))
            tipVolumeText.text(`Volume: ${nearestPointVolume.value} ${this.currency}`)

            // Change X position of the tip
            tip.attr('transform', `translate(${x(nearestPointPrice.time)})`)

            // Change Y position of the circle
            tipCircle.attr('cy', y(nearestPointPrice.value))
          })
        }

        for (const event of ['mouseout', 'touchout']) {
          motionCaptureArea.on(event, function () {
            tip.classed(`${className}__tip--show`, false)
          })
        }

        tip.classed(`${className}__tip--hidden`, true)

        // window.setTimeout(() => motionCaptureArea.dispatchEvent('mousemove'), 200)
      }
    }
  }
</script>

<style lang="scss">
  @import "~scss/variables";

  .chart-root,
  .chart-root svg {
    @media (min-width: 767px) {
      min-height: 230px;
    }
    @media (max-width: 767px) {
      padding-bottom: 40px;
    }
    transition: .2s;
  }

  svg.chart {
    display: block;
    overflow: visible;

    * { font-family: inherit; }
    & > g { overflow: hidden; }
  }

  .chart__area {
    fill: rgba($col-md-primary, .35);
    opacity: 0;
  }

  .chart__line {
    fill: none;
    stroke-width: 2px;
    stroke: $col-md-primary;
    stroke-linecap: round;
  }

  .chart__line1 {
    fill: none;
    stroke-width: 2px;
    stroke: blue;
    stroke-linecap: round;
  }

  .chart__x-axis {
    text {
      font-size: $fs-root;
      fill: $col-text;
    }
    .domain { display: none; }
  }

  .chart__y-axis {
    text {
      font-size: $fs-root;
      fill: $col-text;
    }
    line {
      stroke-dasharray: 3 3;
      stroke: $col-border;
      opacity: .4;
    }
    .domain { display: none; }
  }

  .chart__tip {
    transition: opacity .2s;
    opacity: 0;
    &--show { opacity: 1; }
    &--hidden { opacity: 0!important; }
  }
  .chart__tip-line {
    stroke-width: 1px;
    stroke: $col-md-primary;
  }

  .chart__tip-circle {
    stroke-width: 2px;
    stroke: rgba($col-md-primary, .35);
    fill: $col-md-primary;
  }

  .chart__tip-text-box {
    fill: rgba($col-md-primary, .35);
    stroke: $col-md-primary;
    stroke-width: 1px;
  }

  .chart__tip-text-price {
    font-size: $fs-root;
    fill: $col-md-primary;
    font-weight: 800;
  }

  .chart__tip-text-time {
    font-size: 0.8rem;
    fill: $col-md-primary;
  }

  .chart__tip-motion-capture-area {
    opacity: 0;
  }
</style>



// WEBPACK FOOTER //
// src/vue/app/trade/index/components/chart/ChartWidget.ChartRenderer.vue